/** CRM contact channel types (aligned with ATW backend). */

import { coerceCountryCodeId, getCountryCodeById } from "@/lib/countryCodes";
import {
  contactAddressesToApiBody,
  normalizeContactAddressesInput,
  type CrmAddressTypeOptions,
  type CrmContactAddress,
} from "@/lib/crmContactAddresses";
import type {
  CrmLeadFieldDefinition,
  CrmLeadFieldSectionId,
  CrmLeadModuleFieldsDocument,
} from "@/lib/crmLeadModuleFields";
import { crmLeadFieldIsHidden, seedStandardCrmLeadModuleFields } from "@/lib/crmLeadModuleFields";

export type CrmContactEmailType = "company" | "business" | "personal" | "other";
export type CrmContactPhoneType = "mobile" | "home" | "business" | "fax" | "other";
export type CrmContactSocialPlatform =
  | "linkedin"
  | "twitter"
  | "facebook"
  | "instagram"
  | "youtube"
  | "github"
  | "website"
  | "other";

export type CrmContactEmailContext = "account" | "contact";

export type CrmContactEmail = {
  type: CrmContactEmailType;
  address: string;
  isPrimary?: boolean;
};

export type CrmContactPhone = {
  type: CrmContactPhoneType;
  countryId: string;
  number: string;
  /** UI-only until backend stores SMS opt-in per phone row. */
  smsOptIn?: boolean;
};

export type CrmContactSocial = {
  platform: CrmContactSocialPlatform;
  url: string;
  isPrimary?: boolean;
};

export const CRM_ACCOUNT_EMAIL_TYPES: CrmContactEmailType[] = ["company", "business", "personal"];
export const CRM_CONTACT_EMAIL_TYPES: CrmContactEmailType[] = ["business", "personal"];

export const CRM_CONTACT_PHONE_TYPES: CrmContactPhoneType[] = ["mobile", "home", "business", "fax"];

export const CRM_CONTACT_SOCIAL_PLATFORMS: CrmContactSocialPlatform[] = [
  "linkedin",
  "twitter",
  "facebook",
  "instagram",
  "youtube",
  "github",
  "website",
];

export type CrmContactChannelsState = {
  emails: CrmContactEmail[];
  phones: CrmContactPhone[];
  socials: CrmContactSocial[];
  addresses: CrmContactAddress[];
};

export type CrmChannelTypeOptions = {
  emailTypes: CrmContactEmailType[];
  emailLabels: Partial<Record<CrmContactEmailType, string>>;
  socialPlatforms: CrmContactSocialPlatform[];
  socialIconUrls: Partial<Record<CrmContactSocialPlatform, string>>;
};

export function emailTypesForContext(context: CrmContactEmailContext): CrmContactEmailType[] {
  return context === "account" ? [...CRM_ACCOUNT_EMAIL_TYPES] : [...CRM_CONTACT_EMAIL_TYPES];
}

export function defaultEmailTypeForContext(context: CrmContactEmailContext): CrmContactEmailType {
  return context === "account" ? "company" : "business";
}

export function emptyEmailRow(context: CrmContactEmailContext = "contact"): CrmContactEmail {
  return { type: defaultEmailTypeForContext(context), address: "", isPrimary: true };
}

export function emptyPhoneRow(countryId = "us"): CrmContactPhone {
  return {
    type: "mobile",
    countryId: coerceCountryCodeId(countryId),
    number: "",
    smsOptIn: false,
  };
}

export function defaultPhoneCountryIdFromLeadFields(
  sections: CrmLeadModuleFieldsDocument["sections"] | null | undefined,
  sectionId: Extract<CrmLeadFieldSectionId, "account_lead" | "contact_lead">,
): string {
  const fields: CrmLeadFieldDefinition[] = sections?.[sectionId]?.length
    ? sections[sectionId]
    : seedStandardCrmLeadModuleFields()[sectionId];
  const row = fields.find((f) => f.kind === "phone_country_default");
  return coerceCountryCodeId(row?.optionValue, "us");
}

export function emptySocialRow(): CrmContactSocial {
  return { platform: "linkedin", url: "", isPrimary: true };
}

export function emptyChannelsState(context: CrmContactEmailContext = "contact"): CrmContactChannelsState {
  return {
    emails: [emptyEmailRow(context)],
    phones: [],
    socials: [],
    addresses: [],
  };
}

function normalizeEmailType(value: unknown, context: CrmContactEmailContext): CrmContactEmailType {
  const v = String(value || "").trim().toLowerCase();
  if (v === "company" && context === "account") return "company";
  if (v === "business") return "business";
  if (v === "personal") return "personal";
  if (v === "other") return context === "account" ? "company" : "business";
  return defaultEmailTypeForContext(context);
}

function normalizeEmailAddress(value: unknown): string {
  return String(value ?? "").trim().toLowerCase();
}

function ensureSinglePrimaryEmail(emails: CrmContactEmail[]): CrmContactEmail[] {
  if (!emails.length) return emails;
  const primaries = emails.filter((e) => e.isPrimary);
  if (primaries.length === 1) return emails;
  const preferred =
    emails.find((e) => e.isPrimary) ||
    emails.find((e) => e.type === "company") ||
    emails.find((e) => e.type === "business") ||
    emails.find((e) => e.type === "personal") ||
    emails[0];
  return emails.map((e) => ({
    ...e,
    isPrimary: e.address === preferred.address,
  }));
}

export function phoneRowToE164(phone: CrmContactPhone): string {
  const national = phone.number.trim();
  if (!national) return "";
  if (national.startsWith("+")) return national;
  const country = getCountryCodeById(phone.countryId) || getCountryCodeById("us");
  const digits = national.replace(/\D/g, "");
  if (!digits) return national;
  if (!country) return `+${digits}`;
  const codeDigits = country.code.replace(/^\+/, "");
  if (digits.startsWith(codeDigits)) return `${country.code}${digits.slice(codeDigits.length)}`;
  return `${country.code}${digits}`;
}

export function pickLegacyEmailFromChannels(emails: CrmContactEmail[]): string | null {
  const filled = emails.filter((e) => e.address.trim());
  if (!filled.length) return null;
  const primary = filled.find((e) => e.isPrimary);
  const company = filled.find((e) => e.type === "company");
  const business = filled.find((e) => e.type === "business");
  const personal = filled.find((e) => e.type === "personal");
  const pick = primary || company || business || personal || filled[0];
  return normalizeEmailAddress(pick.address) || null;
}

export function pickLegacyPhoneFromChannels(phones: CrmContactPhone[]): string | null {
  const filled = phones.filter((p) => p.number.trim());
  if (!filled.length) return null;
  const mobile = filled.find((p) => p.type === "mobile");
  const business = filled.find((p) => p.type === "business");
  const pick = mobile || business || filled[0];
  return phoneRowToE164(pick) || pick.number.trim() || null;
}

export function pickLegacyLinkedinFromChannels(socials: CrmContactSocial[]): string | null {
  const linkedin = socials.filter((s) => s.platform === "linkedin" && s.url.trim());
  if (!linkedin.length) return null;
  const primary = linkedin.find((s) => s.isPrimary) || linkedin[0];
  return primary.url.trim() || null;
}

export function contactChannelsToApiBody(channels: CrmContactChannelsState): {
  emails: CrmContactEmail[];
  phones: Omit<CrmContactPhone, "smsOptIn">[];
  socials: CrmContactSocial[];
  addresses: CrmContactAddress[];
  email: string | null;
  phone: string | null;
  linkedinUrl: string | null;
} {
  const emails = ensureSinglePrimaryEmail(
    channels.emails.filter((e) => e.address.trim()).map((e) => ({
      ...e,
      address: normalizeEmailAddress(e.address),
    })),
  );
  const phones = channels.phones.filter((p) => p.number.trim());
  const socials = channels.socials.filter((s) => s.url.trim());
  const addresses = contactAddressesToApiBody(channels.addresses);

  return {
    emails,
    phones: phones.map(({ smsOptIn: _sms, ...rest }) => rest),
    socials,
    addresses,
    email: pickLegacyEmailFromChannels(emails),
    phone: pickLegacyPhoneFromChannels(phones),
    linkedinUrl: pickLegacyLinkedinFromChannels(socials),
  };
}

export type { CrmAddressTypeOptions, CrmContactAddress };

export function channelsStateFromContact(contact: {
  emails?: CrmContactEmail[];
  phones?: CrmContactPhone[];
  socials?: CrmContactSocial[];
  addresses?: CrmContactAddress[];
  email?: string | null;
  phone?: string | null;
  linkedinUrl?: string | null;
}): CrmContactChannelsState {
  let emails = contact.emails?.length ? [...contact.emails] : [];
  let phones = contact.phones?.length ? [...contact.phones] : [];
  let socials = contact.socials?.length ? [...contact.socials] : [];
  let addresses = normalizeContactAddressesInput(contact.addresses);

  if (!emails.length && contact.email?.trim()) {
    emails = [{ type: "business", address: normalizeEmailAddress(contact.email), isPrimary: true }];
  }
  if (!phones.length && contact.phone?.trim()) {
    phones = [emptyPhoneRow("us")];
    phones[0].number = contact.phone.trim();
  }
  if (!socials.length && contact.linkedinUrl?.trim()) {
    socials = [{ platform: "linkedin", url: contact.linkedinUrl.trim(), isPrimary: true }];
  }
  if (!emails.length) emails = [emptyEmailRow("contact")];

  return {
    emails: ensureSinglePrimaryEmail(emails),
    phones,
    socials,
    addresses,
  };
}

/** Client-side email format check for channel rows (non-empty values only). */
export const CRM_EMAIL_ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidCrmEmailAddress(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (trimmed.length > 254) return false;
  return CRM_EMAIL_ADDRESS_PATTERN.test(trimmed);
}

export function stripRedundantEmailWordFromTypeLabel(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;
  const stripped = trimmed.replace(/\s+email\s*$/i, "").trim();
  return stripped || trimmed;
}

export function formatCrmEmailTypeLabel(
  type: CrmContactEmailType,
  context: CrmContactEmailContext = "contact",
): string {
  void context;
  if (type === "company") return "Company";
  if (type === "business") return "Business";
  if (type === "personal") return "Personal";
  return "Other";
}

export function displayCrmEmailTypeLabel(
  raw: string | undefined,
  type: CrmContactEmailType,
  context: CrmContactEmailContext = "contact",
): string {
  const base = raw?.trim() || formatCrmEmailTypeLabel(type, context);
  return stripRedundantEmailWordFromTypeLabel(base);
}

export function firstInvalidCrmChannelEmail(
  channels: CrmContactChannelsState,
): CrmContactEmail | null {
  for (const row of channels.emails) {
    if (row.address.trim() && !isValidCrmEmailAddress(row.address)) return row;
  }
  return null;
}

export function assertValidCrmChannelEmails(
  channels: CrmContactChannelsState,
  contextLabel: string,
): void {
  const invalid = firstInvalidCrmChannelEmail(channels);
  if (invalid) {
    throw new Error(`${contextLabel}: enter a valid email address.`);
  }
}

export function formatCrmPhoneTypeLabel(type: CrmContactPhoneType): string {
  if (type === "mobile") return "Mobile";
  if (type === "home") return "Personal";
  if (type === "business") return "Business";
  if (type === "fax") return "Fax";
  return "Other";
}

export function formatCrmSocialPlatformLabel(platform: CrmContactSocialPlatform): string {
  if (platform === "linkedin") return "LinkedIn";
  if (platform === "twitter") return "Twitter / X";
  if (platform === "facebook") return "Facebook";
  if (platform === "instagram") return "Instagram";
  if (platform === "youtube") return "YouTube";
  if (platform === "github") return "GitHub";
  if (platform === "website") return "Website";
  return "Other";
}

function isEmailType(value: string): value is CrmContactEmailType {
  return value === "company" || value === "business" || value === "personal" || value === "other";
}

function isSocialPlatform(value: string): value is CrmContactSocialPlatform {
  return CRM_CONTACT_SOCIAL_PLATFORMS.includes(value as CrmContactSocialPlatform);
}

export function channelOptionsFromLeadFields(
  sections: CrmLeadModuleFieldsDocument["sections"] | null | undefined,
  sectionId: Extract<CrmLeadFieldSectionId, "account_lead" | "contact_lead">,
  context: CrmContactEmailContext,
): CrmChannelTypeOptions {
  const fallbackSection = seedStandardCrmLeadModuleFields()[sectionId];
  const fields: CrmLeadFieldDefinition[] = sections?.[sectionId]?.length
    ? sections[sectionId]
    : fallbackSection;

  const emailTypes: CrmContactEmailType[] = [];
  const emailLabels: Partial<Record<CrmContactEmailType, string>> = {};
  const socialPlatforms: CrmContactSocialPlatform[] = [];
  const socialIconUrls: Partial<Record<CrmContactSocialPlatform, string>> = {};

  for (const field of fields) {
    if (crmLeadFieldIsHidden(field)) continue;
    if (field.kind === "email_type" && field.optionValue && isEmailType(field.optionValue)) {
      const type = normalizeEmailType(field.optionValue, context);
      if (!emailTypes.includes(type)) emailTypes.push(type);
      const rawLabel = field.optionLabel || field.label || formatCrmEmailTypeLabel(type, context);
      emailLabels[type] = displayCrmEmailTypeLabel(rawLabel, type, context);
    }
    if (field.kind === "social_type" && field.optionValue && isSocialPlatform(field.optionValue)) {
      if (!socialPlatforms.includes(field.optionValue)) socialPlatforms.push(field.optionValue);
      if (field.optionIconUrl?.trim()) {
        socialIconUrls[field.optionValue] = field.optionIconUrl.trim();
      }
    }
  }

  const standardEmail = emailTypesForContext(context);
  const mergedEmail = standardEmail.filter((t) => emailTypes.length === 0 || emailTypes.includes(t));
  const emailList = mergedEmail.length ? mergedEmail : standardEmail;

  return {
    emailTypes: emailList,
    emailLabels,
    socialPlatforms: socialPlatforms.length ? socialPlatforms : [...CRM_CONTACT_SOCIAL_PLATFORMS],
    socialIconUrls,
  };
}

export function mergeEmailTypesForEditor(
  visibleTypes: CrmContactEmailType[],
  rows: CrmContactEmail[],
): CrmContactEmailType[] {
  const merged = [...visibleTypes];
  for (const row of rows) {
    if (!merged.includes(row.type)) merged.push(row.type);
  }
  return merged;
}

export function mergeSocialPlatformsForEditor(
  visiblePlatforms: CrmContactSocialPlatform[],
  rows: CrmContactSocial[],
): CrmContactSocialPlatform[] {
  const merged = [...visiblePlatforms];
  for (const row of rows) {
    if (!merged.includes(row.platform)) merged.push(row.platform);
  }
  return merged;
}

export function coerceEmailRowType(
  type: CrmContactEmailType,
  context: CrmContactEmailContext,
  options: CrmChannelTypeOptions,
): CrmContactEmailType {
  if (options.emailTypes.includes(type)) return type;
  return options.emailTypes[0] || defaultEmailTypeForContext(context);
}

export function coerceSocialPlatform(
  platform: CrmContactSocialPlatform,
  options: CrmChannelTypeOptions,
): CrmContactSocialPlatform {
  if (options.socialPlatforms.includes(platform)) return platform;
  return options.socialPlatforms[0] || "linkedin";
}

export function socialPlatformFromOptionValue(value: string | undefined): CrmContactSocialPlatform {
  const v = String(value || "").trim().toLowerCase();
  if (isSocialPlatform(v)) return v;
  return "other";
}

export function formatCrmPhoneDisplay(phone: CrmContactPhone): string {
  const number = phone.number.trim();
  if (!number) return "";
  const cc = getCountryCodeById(coerceCountryCodeId(phone.countryId));
  const dial = cc?.code?.trim();
  return dial ? `${dial} ${number}` : number;
}
